import type { DestinationsContainer } from '@elara/client-core/containers/DestinationsContainer';
import type { HotelsContainer } from '@elara/client-core/containers/HotelsContainer';
import type { PlacesContainer } from '@elara/client-core/containers/PlacesContainer';
import type { RoomOptionsContainer } from '@elara/client-core/containers/RoomOptionsContainer';
import type { Destination } from '@elara/client-core/models/DestinationModel';
import type { Hotel } from '@elara/client-core/models/HotelModel';
import type { Place } from '@elara/client-core/models/PlaceModel';
import type { RoomOptionModel } from '@elara/client-core/models/RoomOptionModel';
import type { MapPointIn } from '@elara/domain/models/schemas/widget-schemas';

import type { GoogleMapRef, MapPoint } from '#ui/GoogleMap.js';

import React, { useRef, useState } from 'react';

import { cn, parseDocumentId, pluralize } from '@elara/client-core/lib/utils';

import { Env } from '#env.js';
import { useApp, useTripsState } from '#states/hooks.js';
import { GoogleMap } from '#ui/GoogleMap.js';
import { useOpenGuide } from '#ui/guides/guide-scope.js';
import { Button, Card } from '#ui/index.js';

import { WidgetErrorState } from './WidgetErrorState.js';
import { WidgetLoadingState } from './WidgetLoadingState.js';

export interface MapWidgetProps {
  points?: MapPointIn[];
  center?: { latitude?: number; longitude?: number };
  zoom?: number;
  size?: 'sm' | 'md' | 'lg' | 'full';
  className?: string;
}

/**
 * MapWidget displays an interactive map with one or more pins.
 * Uses Google Maps JavaScript SDK with AdvancedMarkerElement for enhanced functionality.
 * Requires VITE_GOOGLE_MAPS_API_KEY and VITE_GOOGLE_MAPS_MAP_ID environment variables.
 *
 * This component wraps the core GoogleMap component with additional UI elements
 * like Card styling, loading states, error states, and a "Fit map to all locations" button.
 *
 * Points may be supplied in either form:
 *   1. Explicit: latitude/longitude (or address) + optional label/description.
 *   2. Reference: referenceType ('place'|'hotel'|'destination'|'room') + referenceId.
 *      The widget hydrates the entity from Firestore and wires the click to open the guide. The pin is
 *      labelled with the entity's own name; the model's label only stands in when it has none (models
 *      tend to decorate it, e.g. "Kitcho (Arashiyama)").
 *
 * @param points - Array of points to display on the map
 * @param center - Optional center point for the map
 * @param zoom - Initial zoom level (default: 12)
 * @param size - Size of the map widget (default: 'md')
 * @param className - Optional CSS class for the container
 */
export function MapWidget({ points, center, zoom = 12, size = 'md', className }: MapWidgetProps) {
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const mapRef = useRef<GoogleMapRef>(null);
  const tripsState = useTripsState();
  // A pin on a map inside a guide follows on from the guide (Back returns); in chat it opens a new one.
  const openGuide = useOpenGuide();
  const { features } = useApp('features');
  const { places, hotels, destinations, roomOptions } = features as {
    places: PlacesContainer;
    hotels: HotelsContainer;
    destinations: DestinationsContainer;
    roomOptions: RoomOptionsContainer;
  };
  const tripId = tripsState.current?.id;

  const mapSizes = { sm: 'h-32', md: 'h-96', lg: 'h-128', full: 'h-full' };
  const mapHeight = mapSizes[size];
  const fillParent = size === 'full';

  // Resolve reference-backed points reactively. We track whether every reference has
  // settled (handle ready AND, for rooms, the lazy hotel hydration is also ready) so we
  // can mount GoogleMap once with the final point set rather than remounting as each
  // entity arrives -- multiple SDK reinitializations race against the guide's map.
  let allReferencesSettled = true;
  const resolvedPoints: MapPoint[] = (points ?? []).flatMap((point): MapPoint[] => {
    if (!point.referenceType || !point.referenceId) {
      return [point as MapPoint];
    }

    const id = parseDocumentId(point.referenceId) ?? point.referenceId;

    if (point.referenceType === 'room' && !tripId) return [];

    const handle =
      point.referenceType === 'place'
        ? places.ClientPlace.get({ id })
        : point.referenceType === 'hotel'
          ? hotels.ClientHotel.get({ id })
          : point.referenceType === 'destination'
            ? destinations.ClientDestination.get({ id })
            : roomOptions.ClientRoomOption.get({ tripId: tripId!, id });

    if (!handle.isReady) {
      allReferencesSettled = false;
      return [];
    }
    if (handle.error || !handle.data) return [];

    let base: MapPoint;
    if (point.referenceType === 'room') {
      const room = handle.data as RoomOptionModel;
      const hotelMapPoint = room.hotel?.mapPoint;
      if (!hotelMapPoint) {
        allReferencesSettled = false;
        return [];
      }
      base = { ...hotelMapPoint, label: room.displayName };
    } else {
      base = (handle.data as Place | Hotel | Destination).mapPoint;
    }

    const referenceType = point.referenceType;
    return [
      {
        ...base,
        id: point.id ?? base.id,
        label: base.label ?? point.label,
        description: point.description ?? base.description,
        onClick: () => openGuide(referenceType, id),
      },
    ];
  });

  const validPointsCount = resolvedPoints.filter((point) => {
    const hasCoordinates =
      typeof point.latitude === 'number' &&
      typeof point.longitude === 'number' &&
      point.latitude >= -90 &&
      point.latitude <= 90 &&
      point.longitude >= -180 &&
      point.longitude <= 180;
    const hasAddress = typeof point.address === 'string' && point.address.trim().length > 0;
    return hasCoordinates || hasAddress;
  }).length;

  const fitBoundsToAllMarkers = () => {
    mapRef.current?.fitBounds();
  };

  if (!points || points.length === 0) {
    console.warn('[MapWidget] No points provided');
    return null;
  }

  // Wait for all reference-backed points to settle before mounting the map. This avoids
  // remounting GoogleMap as entities arrive, which would race the guide's map SDK init.
  if (!allReferencesSettled) {
    return (
      <div className={cn('flex flex-col w-full bg-background-tertiary rounded-md', fillParent && 'h-full', className)}>
        <div className={cn('overflow-hidden', fillParent && 'h-full')}>
          <div className={cn('relative flex items-center justify-center w-full', mapHeight)}>
            <WidgetLoadingState message="Loading map..." />
          </div>
        </div>
      </div>
    );
  }

  if (validPointsCount === 0) {
    console.warn('[MapWidget] No valid points provided');
    return null;
  }

  return (
    <div className={cn('flex flex-col w-full', fillParent && 'h-full', className)}>
      <Card className={cn('overflow-hidden', fillParent && 'h-full')}>
        <div className={cn('relative w-full', mapHeight)}>
          {Env.VITE_GOOGLE_MAPS_API_KEY ? (
            error ? (
              <WidgetErrorState message={error} />
            ) : (
              <>
                <GoogleMap
                  ref={mapRef}
                  points={resolvedPoints}
                  center={
                    center?.latitude !== undefined && center?.longitude !== undefined
                      ? { latitude: center.latitude, longitude: center.longitude }
                      : undefined
                  }
                  zoom={zoom}
                  className="w-full h-full"
                  onError={(errorMsg) => {
                    setError(errorMsg);
                  }}
                  onLoadingChange={(loading) => {
                    setIsLoading(loading);
                  }}
                />
                {isLoading && <WidgetLoadingState message="Loading map..." />}
              </>
            )
          ) : (
            <WidgetErrorState message="Google Maps API Key Required" />
          )}
        </div>
      </Card>

      {/* View all locations button - only show when there are multiple points */}
      {validPointsCount > 1 && !isLoading && !error && Env.VITE_GOOGLE_MAPS_API_KEY && (
        <div className="mt-1 flex justify-between">
          <span className="text-sm text-muted-foreground p-2">
            {validPointsCount} {pluralize(validPointsCount, 'location')}
          </span>
          <Button onClick={fitBoundsToAllMarkers} variant="link" size="sm">
            Fit map to all locations
          </Button>
        </div>
      )}
    </div>
  );
}
